/**
 * MealPlanSummaryCard — Phase 2.2B Session 4
 * Shows the currently-active meal plan version with totals + plan type + version + delta vs target.
 */
import React from 'react';
import { Utensils, History, Sparkles } from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';

const PLAN_BADGE = {
  GENERIC: 'bg-zinc-700 text-zinc-200 border-zinc-600',
  REST: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  TRAINING: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
  MATCH: 'bg-red-500/20 text-red-300 border-red-500/40',
  DOUBLE_SESSION: 'bg-violet-500/20 text-violet-300 border-violet-500/40',
  RTP: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
};

const fmt0 = (v) => v === null || v === undefined ? '—' : Math.round(Number(v));
const fmt1 = (v) => v === null || v === undefined ? '—' : Number(v).toFixed(1);

const DeltaInline = ({ value, suffix }) => {
  if (value === null || value === undefined) return null;
  const abs = Math.abs(value);
  if (abs < 0.5) return <span className="text-emerald-400 text-[11px]">≈ target</span>;
  const color = value > 0 ? 'text-amber-400' : 'text-cyan-400';
  return <span className={`${color} text-[11px]`}>{value > 0 ? '+' : ''}{value.toFixed(1)}{suffix}</span>;
};

const StatBox = ({ label, value, delta, suffix, testid }) => (
  <div className="bg-zinc-950/50 border border-zinc-800 rounded p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
    <div className="text-xl font-bold text-zinc-100">{value}</div>
    <div className="mt-1"><DeltaInline value={delta} suffix={suffix} /></div>
  </div>
);

const MealPlanSummaryCard = ({ version, plan, canEdit = false, onNewVersion, dayType, fallbackToGeneric }) => {
  const { language } = useLanguage();
  const totals = version?.totals || {};
  const delta = version?.totals_vs_target_delta || {};

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="mealplan-summary-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Utensils className="w-5 h-5 text-amber-400" />
          <h3 className="text-lg font-semibold text-zinc-100">
            {language === 'IT' ? 'Piano alimentare attivo' : 'Active meal plan'}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {version && (
            <>
              <Badge className={`${PLAN_BADGE[version.plan_type] || ''} text-xs`} data-testid="mealplan-plan-type-badge">
                {version.plan_type}
              </Badge>
              <Badge variant="outline" className="border-zinc-700 text-zinc-300 text-xs" data-testid="mealplan-version-badge">
                v{version.version}
              </Badge>
              {dayType && (
                <Badge className="bg-zinc-800 text-zinc-300 border-zinc-700 text-xs" data-testid="mealplan-day-type">
                  {language === 'IT' ? 'Oggi' : 'Today'}: {dayType}
                </Badge>
              )}
              {fallbackToGeneric && (
                <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40 text-[10px]" data-testid="mealplan-fallback">
                  {language === 'IT' ? 'Fallback Generic' : 'Generic fallback'}
                </Badge>
              )}
            </>
          )}
          {canEdit && (
            <Button size="sm" onClick={onNewVersion} data-testid="mealplan-new-version-shortcut"
              className="bg-amber-600 hover:bg-amber-500 text-white">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              {language === 'IT' ? 'Nuova versione' : 'New version'}
            </Button>
          )}
        </div>
      </div>

      {!version ? (
        <div className="text-sm text-zinc-500 py-4 text-center" data-testid="mealplan-empty">
          {language === 'IT' ? 'Nessun piano alimentare ancora creato.' : 'No meal plan created yet.'}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatBox testid="mealplan-totals-calories" label={language === 'IT' ? 'Calorie' : 'Calories'}
              value={`${fmt0(totals.calories)} kcal`} delta={delta.calories} suffix=" kcal" />
            <StatBox testid="mealplan-totals-protein" label={language === 'IT' ? 'Proteine' : 'Protein'}
              value={`${fmt1(totals.protein_g)} g`} delta={delta.protein_g} suffix=" g" />
            <StatBox testid="mealplan-totals-carbs" label={language === 'IT' ? 'Carboidrati' : 'Carbs'}
              value={`${fmt1(totals.carbs_g)} g`} delta={delta.carbs_g} suffix=" g" />
            <StatBox testid="mealplan-totals-fats" label={language === 'IT' ? 'Grassi' : 'Fats'}
              value={`${fmt1(totals.fats_g)} g`} delta={delta.fats_g} suffix=" g" />
          </div>
          {version.label && (
            <div className="mt-3 text-xs text-zinc-400">
              <span className="text-zinc-500">{language === 'IT' ? 'Etichetta' : 'Label'}:</span> {version.label}
            </div>
          )}
          <div className="mt-2 text-[11px] text-zinc-500 flex items-center gap-1">
            <History className="w-3 h-3" />
            {language === 'IT' ? 'Valido dal' : 'Effective from'}: {version.effective_from}
            {version.effective_to ? ` → ${version.effective_to}` : ''}
            {plan?.versions_count > 1 && (
              <span className="ml-2">· {plan.versions_count} {language === 'IT' ? 'versioni in totale' : 'versions total'}</span>
            )}
          </div>
        </>
      )}
    </Card>
  );
};

export default MealPlanSummaryCard;
